import {
  Chip,
  alpha,
  Paper,
  Stack,
  Select,
  Tooltip,
  MenuItem,
  useTheme,
  IconButton,
  Typography,
  InputLabel,
  FormControl,
  ListItemText,
  ToggleButton,
  ToggleButtonGroup,
} from '@mui/material';
import { useEffect, useId, useRef } from 'react';
import type { DraftMultiSortRule } from './types';
import type { SelectChangeEvent } from '@mui/material';
import { FormattedMessage, useIntl } from 'react-intl';
import type { MultiSortColumnOption, MultiSortDirection } from './model';
import ArrowUpwardRoundedIcon from '@mui/icons-material/ArrowUpwardRounded';
import ArrowDownwardRoundedIcon from '@mui/icons-material/ArrowDownwardRounded';
import DeleteOutlineRoundedIcon from '@mui/icons-material/DeleteOutlineRounded';
import KeyboardArrowUpRoundedIcon from '@mui/icons-material/KeyboardArrowUpRounded';
import KeyboardArrowDownRoundedIcon from '@mui/icons-material/KeyboardArrowDownRounded';

type MultiSortRuleCardProps = {
  rule: DraftMultiSortRule;
  index: number;
  totalRules: number;
  columnOptions: readonly MultiSortColumnOption[];
  selectedFields: ReadonlySet<string>;
  disabled: boolean;
  focusRequestId: number | null;
  onFieldChange: (field: string) => void;
  onDirectionChange: (direction: MultiSortDirection) => void;
  onMove: (direction: -1 | 1) => void;
  onRemove: () => void;
};

const MultiSortRuleCard = ({
  rule,
  index,
  totalRules,
  columnOptions,
  selectedFields,
  disabled,
  focusRequestId,
  onFieldChange,
  onDirectionChange,
  onMove,
  onRemove,
}: MultiSortRuleCardProps) => {
  const intl = useIntl();
  const theme = useTheme();
  const selectLabelId = useId();
  const cardRef = useRef<HTMLDivElement>(null);
  const currentColumn = columnOptions.find(({ field }) => field === rule.field);
  const columnLabel = currentColumn?.label ?? rule.field;
  const selectableColumns = columnOptions.filter(
    ({ field, addable }) => field === rule.field || (addable && !selectedFields.has(field))
  );

  useEffect(() => {
    if (focusRequestId == null) return;

    cardRef.current?.scrollIntoView?.({ block: 'nearest' });
    cardRef.current?.querySelector<HTMLElement>('[role="combobox"]')?.focus();
  }, [focusRequestId]);

  const removeLabel = intl.formatMessage(
    {
      id: 'datagrid.multiSort.remove',
      defaultMessage: '{column} ölçütünü sil',
    },
    { column: columnLabel }
  );
  const moveUpLabel = intl.formatMessage(
    {
      id: 'datagrid.multiSort.moveUp',
      defaultMessage: '{column} ölçütünü yukarı taşı',
    },
    { column: columnLabel }
  );
  const moveDownLabel = intl.formatMessage(
    {
      id: 'datagrid.multiSort.moveDown',
      defaultMessage: '{column} ölçütünü aşağı taşı',
    },
    { column: columnLabel }
  );

  const renderColumnValue = (field: unknown) => {
    const option = columnOptions.find((column) => column.field === field);

    return (
      <Stack direction="row" spacing={1} alignItems="center" width="100%" minWidth={0} overflow="hidden">
        <Tooltip title={option?.label ?? String(field)} placement="top" enterDelay={700}>
          <Typography component="span" variant="body2" noWrap flex={1} minWidth={0}>
            {option?.label ?? String(field)}
          </Typography>
        </Tooltip>
        {option?.hidden && (
          <Chip
            size="small"
            variant="outlined"
            label={intl.formatMessage({ id: 'datagrid.multiSort.hidden', defaultMessage: 'Gizli' })}
            sx={{ height: 20, '& .MuiChip-label': { px: 0.75 } }}
          />
        )}
      </Stack>
    );
  };

  return (
    <Paper
      ref={cardRef}
      role="listitem"
      aria-label={intl.formatMessage(
        {
          id: 'datagrid.multiSort.ruleLabel',
          defaultMessage: '{order}. öncelik: {column}',
        },
        { order: index + 1, column: columnLabel }
      )}
      variant="outlined"
      sx={{
        p: 1.5,
        borderRadius: 2.5,
        borderColor: 'divider',
        transition: theme.transitions.create(['border-color', 'box-shadow']),
        '&:focus-within': {
          borderColor: 'primary.main',
          boxShadow: `0 0 0 3px ${alpha(theme.palette.primary.main, 0.1)}`,
        },
      }}>
      <Stack direction="row" spacing={1} alignItems="center">
        <Chip
          size="small"
          color="primary"
          label={index + 1}
          aria-label={intl.formatMessage(
            {
              id: 'datagrid.multiSort.priority',
              defaultMessage: '{order}. öncelik',
            },
            { order: index + 1 }
          )}
          sx={{ width: 32, flexShrink: 0, fontWeight: 700 }}
        />

        <FormControl fullWidth size="small">
          <InputLabel id={selectLabelId}>
            <FormattedMessage id="datagrid.multiSort.column" defaultMessage="Sütun" />
          </InputLabel>
          <Select
            disabled={disabled}
            labelId={selectLabelId}
            value={rule.field}
            label={intl.formatMessage({ id: 'datagrid.multiSort.column', defaultMessage: 'Sütun' })}
            renderValue={renderColumnValue}
            onChange={(event: SelectChangeEvent) => onFieldChange(event.target.value)}
            MenuProps={{
              PaperProps: {
                sx: { maxHeight: 320, maxWidth: 'calc(100vw - 32px)' },
              },
            }}>
            {selectableColumns.map((column) => (
              <MenuItem key={column.field} value={column.field}>
                <ListItemText primary={column.label} primaryTypographyProps={{ noWrap: true }} sx={{ minWidth: 0 }} />
                {column.hidden && (
                  <Chip
                    size="small"
                    variant="outlined"
                    label={intl.formatMessage({ id: 'datagrid.multiSort.hidden', defaultMessage: 'Gizli' })}
                  />
                )}
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        <Tooltip title={removeLabel}>
          <span>
            <IconButton
              size="small"
              disabled={disabled}
              onClick={onRemove}
              aria-label={removeLabel}
              sx={{ width: 40, height: 40, flexShrink: 0 }}>
              <DeleteOutlineRoundedIcon fontSize="small" />
            </IconButton>
          </span>
        </Tooltip>
      </Stack>

      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} mt={1.25} alignItems="stretch">
        <ToggleButtonGroup
          exclusive
          fullWidth
          size="small"
          disabled={disabled}
          value={rule.sort}
          onChange={(_, value: MultiSortDirection | null) => value && onDirectionChange(value)}
          aria-label={intl.formatMessage(
            {
              id: 'datagrid.multiSort.directionFor',
              defaultMessage: '{column} sıralama yönü',
            },
            { column: columnLabel }
          )}
          sx={{ flex: 1, boxShadow: 'none' }}>
          <ToggleButton
            value="asc"
            disabled={!currentColumn?.directions.includes('asc')}
            aria-label={intl.formatMessage({ id: 'datagrid.multiSort.ascending', defaultMessage: 'Artan' })}>
            <ArrowUpwardRoundedIcon fontSize="small" sx={{ mr: 0.75 }} />
            <FormattedMessage id="datagrid.multiSort.ascending" defaultMessage="Artan" />
          </ToggleButton>
          <ToggleButton
            value="desc"
            disabled={!currentColumn?.directions.includes('desc')}
            aria-label={intl.formatMessage({ id: 'datagrid.multiSort.descending', defaultMessage: 'Azalan' })}>
            <ArrowDownwardRoundedIcon fontSize="small" sx={{ mr: 0.75 }} />
            <FormattedMessage id="datagrid.multiSort.descending" defaultMessage="Azalan" />
          </ToggleButton>
        </ToggleButtonGroup>

        <Stack direction="row" spacing={0.5} justifyContent={{ xs: 'flex-end', sm: 'initial' }}>
          <Tooltip title={moveUpLabel}>
            <span>
              <IconButton
                size="small"
                disabled={disabled || index === 0}
                onClick={() => onMove(-1)}
                aria-label={moveUpLabel}
                sx={{ width: 40, height: 40 }}>
                <KeyboardArrowUpRoundedIcon />
              </IconButton>
            </span>
          </Tooltip>

          <Tooltip title={moveDownLabel}>
            <span>
              <IconButton
                size="small"
                disabled={disabled || index === totalRules - 1}
                onClick={() => onMove(1)}
                aria-label={moveDownLabel}
                sx={{ width: 40, height: 40 }}>
                <KeyboardArrowDownRoundedIcon />
              </IconButton>
            </span>
          </Tooltip>
        </Stack>
      </Stack>
    </Paper>
  );
};

export default MultiSortRuleCard;
